/**
 * Poll `read` until it yields a value, the time runs out, or `shouldAbort` says stop.
 *
 * Built for Capture's per-shot recorder. A retake mounts Capture with a 1 s prep, and the
 * recorder only exists once Preview has sized its recording canvas. On a slow preview
 * that lands AFTER the shot has begun: the loop saw no recorder, skipped `start()`, the
 * recorder appeared a moment later, and `stop()` at the shutter returned nothing, so the
 * retaken slot had no clip (sessions pkohal / p0kaho: 5 of 189 retakes). Waiting a
 * bounded moment for it closes that gap without holding a guest on a booth whose
 * recorder will never come.
 *
 * Pure and import-free so it tests without the renderer.
 */
export interface WaitForValueOptions {
  /** Give up after this long and resolve with the last read (null/undefined). */
  timeoutMs: number
  /** Delay between reads. */
  intervalMs?: number
  /** Checked before every read; `true` resolves immediately with the current read. */
  shouldAbort?: () => boolean
}

export interface WaitForValueResult<T> {
  value: T | null
  /** How long it waited. 0 when the value was already there. */
  waitedMs: number
}

export async function waitForValue<T>(
  read: () => T | null | undefined,
  { timeoutMs, intervalMs = 50, shouldAbort }: WaitForValueOptions
): Promise<WaitForValueResult<T>> {
  const start = Date.now()
  let value = read() ?? null
  while (value === null && !shouldAbort?.() && Date.now() - start < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
    value = read() ?? null
  }
  return { value, waitedMs: Date.now() - start }
}
